/**
 * 모드별 셀 시각 전략 — 단일 진실 소스 (Single Source of Truth)
 *
 * 4가지 뷰 모드(전체, 합계불일치, 변경사항, 변경+합계)에 따라
 * DsdCell에 전달할 시각 속성을 결정하는 순수 함수.
 *
 * ── 셀 선택 시 렌더링 스펙 ──
 *
 * Selection Overlay 우선순위: sumCaution > sumRole > change
 *   합계불일치 + any  → slate "합계 불일치"
 *   합계 + any        → orange "합계"
 *   none + 변경       → indigo/red/lime
 *
 * 선택 시 배경색 (Mode A: default / sum_diffs_and_changes):
 *   합계 + any        → 흰색 (항상)
 *   합계불일치 + eq   → dark (primary-base)
 *   합계불일치 + 변경 → 변경색 (변경 bg 우선)
 *
 * 선택 시 배경색 (Mode C: sum_diffs):
 *   합계 + any        → 흰색 (항상)
 *   합계불일치 + any  → dark (changeStatus→default 강제)
 *
 * 선택 시 배경색 (Mode B: changes):
 *   합계 숨김 → 일반 셀과 동일 (변경색 or 흰색)
 *
 * 선택 시 칩:
 *   합계 selected     → 합계칩+단수칩 숨김 (overlay tab이 대체)
 *   합계불일치        → 단수차이칩 유지
 *
 * 선택 시 Left Indicator:
 *   effectiveSelected → 숨김 (overlay가 상태를 대체)
 *
 * Pair 렌더링:
 *   합계/합계불일치 pair → full selection (자기 데이터 기반, changes 모드 제외)
 *   일반 pair            → plain border (resolvePlainBorder)
 *
 * Children (합계 자식 하이라이트, bg-orange-200):
 *   범위: 선택 셀 + pair 셀 양쪽 children (changes 모드 제외)
 *   Left Indicator: 숨김
 *   단수차이칩 variant: 항상 "dark" (bg-orange-200 위 가독성)
 */

import type {
  ChangeStatus,
  SelectionColor,
  SumStatus,
} from "@/toggle-ui-kit/dsd-table";
import type { DsdDsdViewMode } from "./compare-header";

// ============================================
// 일반 셀 선택 (plain selection)
// ============================================

const PLAIN_SELECTION_BORDER = "border-label-normal";

const selectionBorderMap: Record<SelectionColor, string> = {
  primary: "border-primary-base",
  red: "border-red-600",
  lime: "border-lime-600",
  indigo: "border-indigo-600",
  orange: "border-orange-500",
  slate: "border-slate-500",
};

/** selectionColor → pair 셀 border class */
export function resolvePlainBorder(color?: SelectionColor): string {
  return color ? selectionBorderMap[color] : PLAIN_SELECTION_BORDER;
}

// ============================================
// 입출력 타입
// ============================================

/** 백엔드 셀 데이터에서 추출한 모드 판단용 입력 */
export interface CellModeInput {
  /** 백엔드 status: "equal"|"matched"|"deleted"|"inserted"|"modified" */
  rawStatus: string;
  sumCaution: boolean;
  sumRole: string | null;
  sumDifference: number | null;
}

/** DsdCell에 전달할 최종 시각 속성 */
export interface CellVisualProps {
  changeStatus: ChangeStatus;
  sumStatus: SumStatus;
  cellIndicator: boolean;
  indicatorPriority: "sum" | "change";
  sumDiffLabel: string | undefined;
  showSumChips: boolean;
  sumDiffChipVariant: "dark" | "light";
  selectionColor: SelectionColor | undefined;
  selectionLabel: string | undefined;
}

// ============================================
// 내부 유틸
// ============================================

export const toChangeStatus = (status: string): ChangeStatus => {
  switch (status) {
    case "deleted":
      return "deleted";
    case "inserted":
      return "inserted";
    case "modified":
      return "modified";
    default:
      return "default";
  }
};

const toSumStatus = (
  sumCaution: boolean,
  sumRole: string | null,
): SumStatus => {
  if (sumCaution) return "sum-mismatch";
  if (sumRole === "parent") return "sum-match";
  return "default";
};

const formatSumDiffLabel = (diff: number | null): string | undefined => {
  if (diff == null || diff === 0) return undefined;
  return (diff > 0 ? "+" : "") + diff.toLocaleString();
};

const hasChange = (status: string): boolean =>
  status === "deleted" || status === "inserted" || status === "modified";

// ============================================
// 모드별 셀 선택 시각 결정
// ============================================

/**
 * 모드 + 셀 데이터 → 선택 시 표시할 border 색상/라벨.
 *
 * | mode                       | 우선순위                                    |
 * |----------------------------|---------------------------------------------|
 * | default / sum_diffs_and_changes | sumCaution > sumRole > 변경 상태          |
 * | changes                    | 변경 상태만 (합계 관련 숨김)                  |
 * | sum_diffs                  | sumCaution > sumRole > 변경 상태             |
 */
export function resolveSelectionInfo(
  mode: DsdDsdViewMode,
  input: CellModeInput,
): { color: SelectionColor; label: string } | null {
  if (mode === "changes") {
    if (input.rawStatus === "deleted") return { label: "삭제", color: "red" };
    if (input.rawStatus === "inserted") return { label: "추가", color: "lime" };
    if (input.rawStatus === "modified")
      return { label: "변경", color: "indigo" };
    return null;
  }

  if (input.sumCaution) return { label: "합계 불일치", color: "slate" };
  if (input.sumRole === "parent") return { label: "합계", color: "orange" };
  if (mode === "sum_diffs") return null;
  if (input.rawStatus === "deleted") return { label: "삭제", color: "red" };
  if (input.rawStatus === "inserted") return { label: "추가", color: "lime" };
  if (input.rawStatus === "modified") return { label: "변경", color: "indigo" };
  return null;
}

// ============================================
// 모드별 셀 시각 결정
// ============================================

/**
 * Truth Table:
 *
 * |                    | default / sum_diffs_and_changes | changes       | sum_diffs              |
 * |--------------------|--------------------------------|---------------|------------------------|
 * | 배경색 우선         | 변경 > sum                     | 변경 only     | sum 무조건 우선         |
 * | sumCaution+변경    | 변경 bg                        | 변경 bg       | 검은 bg (primary)      |
 * | sumCaution only    | 검은 bg                        | N/A           | 검은 bg                |
 * | Left Indicator     | sum indicator                  | 없음          | 없음                   |
 * | Selection Border   | sum > change                   | change 우선   | sum > change           |
 * | 합계/단수차이 칩    | 표시                           | 숨김          | 표시                   |
 * | 단수차이 variant   | 변경+sum→dark, sum만→light     | N/A           | 항상 light             |
 */
export function resolveCellVisuals(
  mode: DsdDsdViewMode,
  input: CellModeInput,
): CellVisualProps {
  const rawChange = toChangeStatus(input.rawStatus);
  const isChanged = hasChange(input.rawStatus);
  const sumStatus = toSumStatus(input.sumCaution, input.sumRole);
  const sumDiffLabel = formatSumDiffLabel(input.sumDifference);
  const selection = resolveSelectionInfo(mode, input);

  switch (mode) {
    // ── 전체 / 변경+합계: 변경 bg 우선, sum indicator ──
    case "default":
    case "sum_diffs_and_changes":
      return {
        changeStatus: rawChange,
        sumStatus,
        cellIndicator: input.sumCaution && isChanged,
        indicatorPriority: "sum",
        sumDiffLabel,
        showSumChips: true,
        sumDiffChipVariant: input.sumCaution && isChanged ? "dark" : "light",
        selectionColor: selection?.color,
        selectionLabel: selection?.label,
      };

    // ── 변경사항: 변경 only, 합계 숨김, left indicator 없음 ──
    case "changes":
      return {
        changeStatus: rawChange,
        sumStatus: "default",
        cellIndicator: false,
        indicatorPriority: "change",
        sumDiffLabel: undefined,
        showSumChips: false,
        sumDiffChipVariant: "dark",
        selectionColor: selection?.color,
        selectionLabel: selection?.label,
      };

    // ── 합계불일치: sum bg 무조건 우선, 변경 숨김, left indicator 없음 ──
    case "sum_diffs":
      return {
        changeStatus: "default",
        sumStatus,
        cellIndicator: false,
        indicatorPriority: "sum",
        sumDiffLabel,
        showSumChips: true,
        sumDiffChipVariant: "light",
        selectionColor: selection?.color,
        selectionLabel: selection?.label,
      };
  }
}
